/**
 * CetusEd Community Portal — Digital Consent Form
 *
 * COMPLIANCE (Rule 1: every legal claim shown to a family resolves from the
 * CITATIONS registry, which carries a verification STATUS — never typed inline):
 *
 *   - The consent requirement resolves via citeRef('wac-392-172A-03000'),
 *     VERIFIED in the canon: informed parental consent is required before the
 *     initial evaluation/services, and the rule sets NO deadline.
 *
 *   - The retired copy cited "IDEA §300.300" and told a declining family that
 *     the school "may pursue due process under §300.300(b)(4)". Neither claim
 *     exists in the verified canon (the legacy _CITATIONS1315 registry has no
 *     §300.300 entry), and the due-process-override sentence is the dangerous
 *     half: whether a district may override a refusal differs between
 *     evaluation consent and services consent, so a one-line blanket claim to
 *     a family could be the OPPOSITE of the law for their situation.
 *     UNVERIFIED = NOT LAW — the claim was removed, not restated.
 *
 *   - The retired copy also asserted the signature "is legally binding under
 *     the ESIGN Act (15 U.S.C. §7001)" — also absent from the verified canon.
 *     The confirmation modal now states only what the product verifiably does:
 *     record the signature, the timestamp, and the exact text displayed.
 */

import React, { useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { citeRef } from '@cetused/shared';
import { useDocumentDetail, useSignConsent } from '../../api/hooks';
import { Button } from '../../ui/components/Button';
import { Modal } from '../../ui/components/Modal';
import { LoadingSpinner } from '../../ui/components/LoadingSpinner';

export const ConsentForm: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const { data: document, isLoading, error } = useDocumentDetail(documentId || '');
  const signConsent = useSignConsent();

  const [hasReadConsent, setHasReadConsent] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [signature, setSignature] = useState('');

  const handleSign = useCallback(() => {
    if (!documentId || !signature.trim()) return;
    // mutate() routes failures into signConsent.isError, which is rendered
    // below — no swallowed rejection, no invisible failure on a legal signature.
    signConsent.mutate(
      {
        consentId: documentId,
        signature: signature.trim(),
        signedAt: new Date().toISOString(),
      },
      { onSuccess: () => navigate(`/documents/${documentId}`, { replace: true }) },
    );
  }, [documentId, signature, signConsent, navigate]);

  const handleDecline = useCallback(() => {
    if (!documentId) return;
    signConsent.mutate(
      {
        consentId: documentId,
        signature: 'DECLINED',
        signedAt: new Date().toISOString(),
      },
      { onSuccess: () => navigate(`/documents/${documentId}`, { replace: true }) },
    );
  }, [documentId, signConsent, navigate]);

  if (isLoading) return <LoadingSpinner />;

  if (error || !document) {
    return (
      <div className="error-page">
        <h1>Consent Form Not Found</h1>
        <p>The consent form could not be loaded. Please contact your school if you believe this is an error.</p>
        <Link to="/documents">Back to Documents</Link>
      </div>
    );
  }

  return (
    <div className="consent-form-page">
      <nav className="breadcrumb">
        <Link to="/documents">Documents</Link> / <Link to={`/documents/${document.id}`}>{document.title}</Link> / Consent
      </nav>

      <h1>Review and Sign Consent</h1>

      <div className="consent-form-content">
        <section className="consent-text-section">
          <p className="consent-instruction">
            Please read the following consent carefully. Your informed consent is
            required before the school may begin the evaluation or services described
            here ({citeRef('wac-392-172A-03000')}).
          </p>
        </section>

        <section className="consent-options">
          <div className="form-field">
            <label>
              <input
                type="checkbox"
                checked={hasReadConsent}
                onChange={(e) => setHasReadConsent(e.target.checked)}
              />
              I have read and understand the consent information above.
            </label>
          </div>

          <div className="form-field">
            <label htmlFor="signature-input">
              Full Name (electronic signature)
            </label>
            <input
              id="signature-input"
              type="text"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              placeholder="Type your full legal name"
              required
              disabled={!hasReadConsent}
            />
            <p className="field-hint">
              Typing your name above serves as your electronic signature. By signing, you
              acknowledge that you have read and understand the consent form.
            </p>
          </div>
        </section>

        {signConsent.isError && (
          <div className="form-error" role="alert">
            Your response could not be recorded. Please try again or contact your school.
          </div>
        )}

        <div className="consent-actions">
          <Button
            onClick={() => setShowConfirmModal(true)}
            disabled={!hasReadConsent || !signature.trim() || signConsent.isPending}
            isLoading={signConsent.isPending}
          >
            I Consent
          </Button>
          <Button
            variant="danger"
            onClick={handleDecline}
            disabled={signConsent.isPending}
          >
            Decline
          </Button>
        </div>

        <p className="consent-notice">
          <strong>Declining consent:</strong> If you decline, the school cannot proceed with
          the requested evaluation or service ({citeRef('wac-392-172A-03000')}). Ask your
          school for your procedural safeguards notice if you have questions about your
          rights and about what the school may do next.
        </p>
      </div>

      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirm Consent"
      >
        <p>
          You are about to sign: <strong>{document.title}</strong>
        </p>
        <p>
          Signed by: <strong>{signature}</strong>
        </p>
        <p>
          Date: <strong>{new Date().toLocaleDateString()}</strong>
        </p>
        <p className="consent-confirm-notice">
          By confirming, you are signing this document electronically. Your signature is
          recorded together with a timestamp and the exact consent text that was
          displayed to you.
        </p>
        <div className="modal-actions">
          <Button onClick={() => setShowConfirmModal(false)} variant="ghost">
            Cancel
          </Button>
          <Button onClick={handleSign} isLoading={signConsent.isPending}>
            Confirm Signature
          </Button>
        </div>
      </Modal>
    </div>
  );
};
